import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient, { setAuthorizationToken } from '../../utils/axios';
import { useFormValidation } from '../../utils/validation';
import GoogleSignInButton from './GoogleSignInButton';
import './auth.css';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const ssoEnabled = searchParams.get('sso') === 'true';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    tenant_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoConfig, setSsoConfig] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ssoEnabled) return;
    apiClient.get('/iam/sso/config')
      .then(res => setSsoConfig(res.data))
      .catch(() => setSsoConfig(null));
  }, [ssoEnabled]);

  const handleSSOSuccess = async (idToken) => {
    if (!formData.tenant_code) {
      setError('Please enter your workspace code first');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/iam/sso/signin', {
        provider: 'google',
        id_token: idToken,
        tenant_code: formData.tenant_code,
      });
      const data = response.data;
      if (data.success) {
        if (data.authorization_token) {
          setAuthorizationToken(data.authorization_token);
        } else if (data.token) {
          setAuthorizationToken(data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/');
      } else {
        setError(data.message || 'SSO sign-in failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'SSO sign-in failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    fieldErrors,
    fieldValid,
    handleBlur: handleValidationBlur,
    handleFieldChange,
    validateAllFields,
    getFieldClass
  } = useFormValidation(['email', 'password', 'tenant_code']);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    handleFieldChange(name, value, newFormData);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    handleValidationBlur(name, formData[name], formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form before submission
    if (!validateAllFields(formData)) {
      setLoading(false);
      setError('Please fix the errors above before submitting');
      return;
    }

    try {
      const response = await apiClient.post('/iam/signin', formData);

      const data = response.data;

      if (data.success) {
        // Identity cookie is set by server; set in-memory authz token if present
        if (data.authorization_token) {
          setAuthorizationToken(data.authorization_token);
        } else if (data.token) {
          setAuthorizationToken(data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/');
      } else {
        setError(data.message || 'Sign-in failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'Sign-in failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>TouchFlow</h1>
          <p>Sign in to your workspace</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className={`form-group ${getFieldClass('tenant_code')}`}>
            <label htmlFor="tenant_code">Workspace</label>
            <input
              id="tenant_code"
              name="tenant_code"
              value={formData.tenant_code}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter workspace code"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.tenant_code && (
                <span className="field-error">{fieldErrors.tenant_code}</span>
              )}
              {fieldValid.tenant_code && !fieldErrors.tenant_code && (
                <span className="field-success">✓ Valid workspace code</span>
              )}
            </div>
          </div>

          <div className={`form-group ${getFieldClass('email')}`}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your email"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
              {fieldValid.email && !fieldErrors.email && (
                <span className="field-success">✓ Valid email address</span>
              )}
            </div>
          </div>

          <div className={`form-group ${getFieldClass('password')}`}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.password && (
                <span className="field-error">{fieldErrors.password}</span>
              )}
              {fieldValid.password && !fieldErrors.password && (
                <span className="field-success">✓ Valid password</span>
              )}
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {ssoEnabled && ssoConfig?.google_client_id && (
          <>
            <div className="sso-divider"><span>or</span></div>
            <GoogleSignInButton
              clientId={ssoConfig.google_client_id}
              onSuccess={handleSSOSuccess}
              onError={(msg) => setError(msg)}
              disabled={loading || !formData.tenant_code}
            />
          </>
        )}

        <div className="auth-footer">
          <p>Don't have an account? Check your email for an invitation link.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 